import React, { useState } from 'react';

import debounce from 'lodash/debounce';

import Button, { LoadingButton } from '@atlaskit/button';
import { ErrorMessage, Field } from '@atlaskit/form';
import { AsyncSelect } from '@atlaskit/select';

import { ModalBody, ModalFooter, ModalHeader } from '@qality/common-components';
import { DEBOUNCE_DELAY_MS, FEATURE_FLAG } from '@qality/common-config';
import { messages } from '@qality/common-messages';
import { TestCycle, TestCycleAsOption } from '@qality/common-model';
import {
  StorageOfUniqueServices,
  TestCyclesService,
} from '@qality/common-service';

type Props = {
  projectId: string;
  onClose: () => void;
  testCaseIds: Array<number | string>;
};

const PAGE_NUMBER = 0;

export default function AddToTestCycleDialogForm({
  projectId,
  onClose,
  testCaseIds,
}: Props): JSX.Element {
  const [selectedCycleId, setSelectedCycleId] = useState<number>();
  const [error, setError] = useState(false);
  const [isLoadingDialog, setIsLoadingDialog] = useState<boolean>(false);

  const handleChange = (newValue: TestCycleAsOption) => {
    setSelectedCycleId(newValue.id);
    setError(false);
  };

  const loadOptions = async (text: string): Promise<TestCycleAsOption[]> => {
    try {
      const response = await TestCyclesService.fetchTestCycles(
        PAGE_NUMBER,
        {
          ...(!FEATURE_FLAG.isPremium && { projectIds: projectId }),
          name: text,
          isClosed: false,
        },
        false,
        false
      );

      const testCycles: Array<TestCycle> = response.testCycles;

      const newOptions = testCycles.map((item: TestCycleAsOption) => {
        item.value = item.id;
        item.label = `(${item.project.key}) ${item.name}`;
        return item;
      });

      return newOptions;
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.testCases.fetchingTestCycles.message']
      );
    }
  };

  const _loadOptions = (
    inputValue: string,
    callback: (options: TestCycleAsOption[]) => void
  ): void => {
    // lodash/debounce doesn't work with async/await
    // eslint-disable-next-line promise/catch-or-return, promise/no-callback-in-promise
    loadOptions(inputValue).then(callback);
  };

  const loadOptionsDebounced = debounce(_loadOptions, DEBOUNCE_DELAY_MS);

  const submit = async () => {
    if (selectedCycleId) {
      setIsLoadingDialog(true);
      try {
        await StorageOfUniqueServices.get('TestCyclesServiceUnique').addToCycle(
          testCaseIds,
          selectedCycleId
        );
        setIsLoadingDialog(false);
        onClose();
      } catch (e) {
        setIsLoadingDialog(false);
        StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
          e,
          messages['error.testCases.addToCycle.message']
        );
      }
    } else {
      setError(true);
      return false;
    }
  };

  return (
    <>
      <ModalHeader title='Add Test Case to Test Cycle' />
      <ModalBody className='add-to-test-cycle-form-field hide-focus-wrapper'>
        <Field label='Select Test Cycle' isRequired name='test-cycle'>
          {() => (
            <>
              <AsyncSelect
                defaultOptions
                loadOptions={loadOptionsDebounced}
                placeholder='Search Test Cycles...'
                onChange={handleChange}
                validationState={error ? 'error' : undefined}
                styles={{
                  option: (provided) => ({
                    ...provided,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }),
                  container: (provided) => ({
                    ...provided,
                    width: '390px',
                  }),
                }}
              />
              {error && <ErrorMessage>This field is required.</ErrorMessage>}
            </>
          )}
        </Field>
      </ModalBody>
      <ModalFooter>
        <Button appearance='subtle' onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          appearance='primary'
          onClick={submit}
          autoFocus
          isLoading={isLoadingDialog}
        >
          Add
        </LoadingButton>
      </ModalFooter>
    </>
  );
}
